import React, { useState, useEffect } from 'react';
import { db, auth } from '../../firebase';
import { ref, onValue, update } from 'firebase/database';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaArrowLeft, FaFilePdf, FaImage } from 'react-icons/fa';

const AuditEngineerDocuments = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState(false);
    const [engineerData, setEngineerData] = useState(null);
    const [status, setStatus] = useState('');
    const [remarks, setRemarks] = useState('');
    const [currentUser, setCurrentUser] = useState(null);

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            if (!user) {
                navigate('/login');
            } else {
                const storedUser = localStorage.getItem('currentUser');
                if (storedUser) {
                    setCurrentUser(JSON.parse(storedUser));
                }
            }
        });

        if (id) {
            const engineerRef = ref(db, `ekamwatapzp/EngineerRegistrations/${id}`);
            const unsubData = onValue(engineerRef, (snapshot) => {
                if (snapshot.exists()) {
                    const data = snapshot.val();
                    setEngineerData(data);
                } else {
                    toast.error("Engineer data not found");
                    navigate('/audit/engineers/new');
                }
                setLoading(false);
            });

            return () => {
                unsubscribe();
                unsubData();
            };
        }
    }, [id, navigate]);

    // Update status and remarks when role or data is loaded
    useEffect(() => {
        if (engineerData && currentUser?.role) {
            const roleKey = currentUser.role.toLowerCase().replace(' ', '');
            const statusField = `${roleKey}Status`;
            const remarkField = `${roleKey}Remark`;
            
            setStatus(engineerData[statusField] || '');
            setRemarks(engineerData[remarkField] || '');
        }
    }, [engineerData, currentUser]);

    const handleUpdateStatus = async () => {
        if (!status) {
            toast.error("Please select a status");
            return;
        }
        if (!currentUser || !currentUser.role) {
            toast.error("User role not found. Please log in again.");
            return;
        }

        setUpdating(true);
        try {
            const roleKey = currentUser.role.toLowerCase().replace(' ', '');
            const statusField = `${roleKey}Status`;
            const remarkField = `${roleKey}Remark`;
            
            const engineerRef = ref(db, `ekamwatapzp/EngineerRegistrations/${id}`);
            const updates = {
                status: status, // Global status
                remarks: remarks, // Global remarks
                statusUpdatedAt: new Date().toISOString(),
                acceptedByRole: currentUser.role,
                [statusField]: status,
                [remarkField]: remarks
            };

            await update(engineerRef, updates);
            toast.success("Status Updated Successfully!");
        } catch (error) {
            toast.error("Error updating status: " + error.message);
        } finally {
            setUpdating(false);
        }
    };

    const renderDocument = (url, label) => {
        if (!url) return <span className="text-gray-400 italic">No Document Found.</span>;
        
        const isPdf = url.toLowerCase().includes('.pdf');
        
        return (
            <a href={url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-blue-600 hover:text-blue-800 transition font-medium">
                {isPdf ? <FaFilePdf className="text-red-500" /> : <img src={url} alt={label} className="w-12 h-12 object-cover rounded border border-gray-100 shadow-sm" />}
                <span className="text-sm underline">{label}</span>
            </a>
        );
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#1a1a4d]"></div>
            </div>
        );
    }

    const documentList = [
        { name: "शासनमान्य संस्थेचे प्रमाणपत्र", url: engineerData?.documents?.['1']?.url },
        { name: " शासनमान्य संस्थेचे शेवटच्या वर्षाचे गुणपत्रक", url: engineerData?.documents?.['2']?.url },
        { name: "अधिवस प्रमाणपत्र", url: engineerData?.documents?.['3']?.url },
        { name: "नाव पत्ता असलेल्या कागदावर स्वतःचा फोटो लावून फोटो स्वसाक्षांकित करावा", url: engineerData?.documents?.['4']?.url },
        { name: "आय कार्ड साईझ फोटो", url: engineerData?.documents?.['5']?.url },
        { name: "अर्जदाराची स्वाक्षरी", url: engineerData?.documents?.['6']?.url },
        { name: "आधार कार्ड", url: engineerData?.documents?.['7']?.url },
        { name: "पॅन कार्ड", url: engineerData?.documents?.['8']?.url },
        { name: "प्रतिज्ञा पत्र", url: engineerData?.documents?.['9']?.url },
        { name: "रोजगार कार्ड", url: engineerData?.documents?.['10']?.url },
        { name: "DSC टोकन", url: engineerData?.documents?.['11']?.url },
        { name: "Other Documents", url: engineerData?.documents?.['12']?.url }
    ];

    const challanList = [
        { name: "नोंदणी चलन आणि फी चलन", url: engineerData?.challanFile || engineerData?.registrationChallanFile }
    ];

    return (
        <div className="p-4 md:p-8 bg-[#f4f7fe] min-h-screen font-sans">
            {/* Header Area */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                <div className="flex items-center gap-4">
                    <button 
                        onClick={() => navigate(-1)}
                        className="p-2.5 bg-white rounded-lg shadow-sm border border-gray-100 text-[#1a1a4d] hover:bg-gray-50 transition active:scale-95"
                    >
                        <FaArrowLeft />
                    </button>
                    <div>
                        <h1 className="text-xl font-bold text-gray-800">अर्जदाराची कागदपत्रे (Audit)</h1>
                        <p className="text-blue-600 font-bold text-lg mt-1">
                            अर्जदार - {engineerData?.firstName} {engineerData?.middleName} {engineerData?.surname}
                        </p>
                    </div>
                </div>
                <div className="text-sm font-medium text-gray-500">
                    सांगली जिल्हा परिषद  /  <span className="text-gray-400">अर्जदाराची कागदपत्रे</span>
                </div>
            </div>

            <div className="space-y-6">
                {/* View All Button */}
                <div>
                    <button className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2 rounded text-sm font-bold shadow-md transition transform active:scale-95">
                        View All Documents
                    </button>
                </div>

                {/* Primary Documents Table */}
                <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
                    <table className="w-full text-left">
                        <thead className="bg-[#f8f9fa] border-b border-gray-200">
                            <tr>
                                <th className="px-6 py-4 text-xs font-black text-gray-500 uppercase tracking-widest w-16">#</th>
                                <th className="px-6 py-4 text-xs font-black text-gray-500 uppercase tracking-widest">Document Name</th>
                                <th className="px-6 py-4 text-xs font-black text-gray-500 uppercase tracking-widest">Document</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {documentList.map((doc, index) => (
                                <tr key={index} className="hover:bg-gray-50 transition">
                                    <td className="px-6 py-4 text-sm font-bold text-gray-700">{index + 1}</td>
                                    <td className="px-6 py-4 text-sm font-bold text-gray-600">{doc.name}</td>
                                    <td className="px-6 py-4">
                                        {renderDocument(doc.url, doc.name)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Challan Table */}
                <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
                    <table className="w-full text-left">
                        <thead className="bg-[#f8f9fa] border-b border-gray-200">
                            <tr>
                                <th className="px-6 py-4 text-xs font-black text-gray-500 uppercase tracking-widest w-16">#</th>
                                <th className="px-6 py-4 text-xs font-black text-gray-500 uppercase tracking-widest">Document Name</th>
                                <th className="px-6 py-4 text-xs font-black text-gray-500 uppercase tracking-widest">Document</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {challanList.map((doc, index) => (
                                <tr key={index} className="hover:bg-gray-50 transition">
                                    <td className="px-6 py-4 text-sm font-bold text-gray-700">{index + 1}</td>
                                    <td className="px-6 py-4 text-sm font-bold text-gray-600">{doc.name}</td>
                                    <td className="px-6 py-4">
                                        {renderDocument(doc.url, doc.name)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Status Update Card */}
                <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-8">
                    <div className="mb-8 overflow-hidden rounded-xl border border-gray-100">
                        <div className="bg-gray-50 px-6 py-4 border-b border-gray-100">
                            <h3 className="font-bold text-gray-800 text-sm uppercase tracking-wider">Status Overview</h3>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 divide-y md:divide-y-0 md:divide-x divide-gray-100">
                            {[
                                { label: 'Assistant', val: engineerData?.assistantStatus },
                                { label: 'Accountant', val: engineerData?.accountantStatus },
                                { label: 'Engineer', val: engineerData?.engineerStatus },
                                { label: 'Admin', val: engineerData?.adminStatus },
                                { label: 'CEO', val: engineerData?.ceoStatus },
                                { label: 'ZP Head', val: engineerData?.zpheadStatus },
                                { label: 'Audit', val: engineerData?.auditStatus }
                            ].map((item, idx) => (
                                <div key={idx} className="p-4 flex flex-col items-center gap-1 group hover:bg-blue-50/50 transition-colors">
                                    <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">{item.label}</span>
                                    <span className={`text-[12px] font-bold uppercase ${
                                        item.val === 'accepted' ? 'text-green-600' : 
                                        item.val === 'rejected' ? 'text-red-600' : 'text-amber-500'
                                    }`}>
                                        {item.val || 'Pending'}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-6">
                        <div>
                            <p className="text-lg font-bold text-gray-800">
                                Incharge Status : - <span className={status === 'accepted' ? 'text-green-600' : status === 'rejected' ? 'text-red-600' : 'text-amber-500'}>
                                    {status || 'Pending'}
                                </span>
                            </p>
                        </div>
                    </div>

                    <div className="space-y-6">
                        <div className="space-y-2">
                            <label className="text-[13px] font-bold text-gray-700">
                                Status <span className="text-red-500">*</span>
                            </label>
                            <select 
                                value={status}
                                onChange={(e) => setStatus(e.target.value)}
                                className="w-full md:w-1/3 border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:border-blue-500 outline-none transition appearance-none bg-no-repeat bg-[right_1rem_center]"
                                style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0 0 24 24\' stroke=\'currentColor\'%3E%3Cpath stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'2\' d=\'M19 9l-7 7-7-7\'/%3E%3C/svg%3E")', backgroundSize: '1em' }}
                            >
                                <option value="">Select Status</option>
                                <option value="accepted">Accepted</option>
                                <option value="rejected">Rejected</option>
                                <option value="pending">Pending</option>
                            </select>
                        </div>

                        <div className="space-y-2">
                            <label className="text-[13px] font-bold text-gray-700">Remarks</label>
                            <textarea 
                                value={remarks}
                                onChange={(e) => setRemarks(e.target.value)}
                                rows="3"
                                className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:border-blue-500 outline-none transition resize-none"
                            ></textarea>
                        </div>

                        <div className="flex items-center gap-3">
                            <button 
                                onClick={handleUpdateStatus}
                                disabled={updating}
                                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-2.5 rounded text-sm font-bold shadow-md transition transform active:scale-95 disabled:opacity-50"
                            >
                                {updating ? 'Updating...' : 'Update Status'}
                            </button>
                            <button 
                                onClick={() => navigate(-1)}
                                className="bg-emerald-500 hover:bg-emerald-600 text-white px-8 py-2.5 rounded text-sm font-bold shadow-md transition transform active:scale-95"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AuditEngineerDocuments;
